import React, { useState, useRef, useEffect, useContext } from "react";
import { useForm, Controller, useFieldArray, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { UserContext } from "../UserContext";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,  
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { putClient } from "../API/clientAPI";
import { getJobOrders, putJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";
import { getTechnicians } from "../API/technicianAPI";
import SignatureCanvas from "react-signature-canvas";
import { patchJobOrderLink } from "../API/jobOrderAPI";
import { getJobOrder } from "../API/jobOrderAPI";
import { sendEmailQueue } from "../API/emailAPI";
import LocationCard from "./FormComponents/LocationCard";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from '@mui/icons-material/Circle';

// Import S3 configurations and bucket names
import {
  s3ReportsPDFUploader,
  REPORTS_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-reports";
import {
  s3InvoicePDFUploader,
  INVOICE_BUCKET_NAME,
} from "../Promotions/config/aws-config-joborder-receipts";
import {
  s3TempBucketUploader,
  TEMP_BUCKET_NAME,
} from "../Promotions/config/aws-config-temp-bucket";

// Import PDF generation functions
import generateReceipts from "./GenerateReceipts";
import generateReports from "./GenerateReports";
import { submitDocumentFieldMapper } from "../API/documentFieldMapperAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

// Pest Count for LocationCard
const PEST_OPTIONS = ["Roaches","Mosq","Rodents","Flies","Ants","Others"];

function MakatiGpcForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  const [jobOrderParameters, setjobOrderParameters] = useState({
    status: "",
    inspection_report: [],
    services: [],
    chemical_used: [],
    equipment_used: [],
    service_fee: "",
    service_report_payment_status: [],
    service_report_payment_method: [],
    job_date: "",
    // end_date: "",
    start_time: "",
    // end_time: "", // removed from front-end since stamped on back-end
    accomplished_by_technician: "",
    reminder_notes: "",
    satisfaction_rating: 0,
  });

  const [client, setClient] = useState({});

  const [editedClient, setEditedClient] = useState();

  const [links, setLinks] = useState({});

  // all techs for the dialog
  const [allTechnicians, setAllTechnicians] = useState([]);
  const [editTechsOpen, setEditTechsOpen] = useState(false);
  const [editSelectedTechIds, setEditSelectedTechIds] = useState([]);

  const [technicianSignatory, setTechnicianSignatory] = useState("");

  const [satisfactionRating, setSatisfactionRating] = useState(0);

  const [controlAgainst, setControlAgainst] = useState({
    crawlingInsects: false,
    flyingInsects:   false,
    rodentControl:   false,
    others:          false,
  });
  const [otherControlText, setOtherControlText] = useState("");

  // Normalize pests into a fixed object shape with numeric values
  const normalizePests = (p) => {
    // base with all six keys = 0
    const base = PEST_OPTIONS.reduce((acc, k) => {
      acc[k] = 0;
      return acc;
    }, {});

    if (Array.isArray(p)) {
      // OLD shape: [{ type, count }]
      for (const { type, count } of p) {
        if (type && Object.prototype.hasOwnProperty.call(base, type)) {
          base[type] = Number(count) || 0;
        }
      }
    } else if (p && typeof p === "object") {
      // NEW shape: { Roaches: 0, Mosq: 0, ... }
      for (const [k, v] of Object.entries(p)) {
        if (Object.prototype.hasOwnProperty.call(base, k)) {
          base[k] = Number(v) || 0;
        }
      }
    }
    return base;
  };

  // helper to sum pests object safely
  const sumPests = (obj = {}) =>
  PEST_OPTIONS.reduce((s, k) => {
    const v = Number(obj?.[k]);
    return s + (isNaN(v) ? 0 : v);
  }, 0);

  // Schema for a single Location row
  const LocationRowSchema = z.object({
    location: z.string().trim().min(1, "Location is required"),
    // allow a sparse/partial pests object; user only needs ONE pest > 0
    pests: z
      .record(z.string(), z.coerce.number().nonnegative())
      .optional()
      .default({}),
    findings: z.string().optional().transform(v => v ?? ""),
    findings_other: z.string().optional().transform(v => v ?? ""),
    recommendation: z.string().optional().transform(v => v ?? ""),
    recommendation_other: z.string().optional().transform(v => v ?? ""),
  }).superRefine((row, ctx) => {
    // ✅ require ONLY: at least one pest type with count > 0
    if (sumPests(row.pests || {}) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["pests"],
        message: "Enter a count for at least one pest type (> 0).",
    });
    }
    // findings
    if (!row.findings && !row.findings_other) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["findings"],
        message: "Please select a Finding or specify Other",
      });
    }
    if (row.findings === "Other" && !row.findings_other.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["findings_other"],
        message: "Please specify other findings",
      });
    }
    // recommendation
    if (!row.recommendation && !row.recommendation_other) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recommendation"],
        message: "Please select a Recommendation or specify Other",
      });
    }
    if (row.recommendation === "Other" && !row.recommendation_other.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["recommendation_other"],
        message: "Please specify other recommendation",
      });
    }
  });

  const MakatiSchema = z.object({
    satisfaction_rating: z.coerce
      .number({
        required_error: "Please pick a rating (1–5).",
        invalid_type_error: "Please pick a rating (1–5).",
      })
      .min(1, { message: "Please pick a rating (1–5)." })
      .max(5, { message: "Please pick a rating (1–5)." }),
    technician_signatory: z.string().trim().min(1, "Please choose a technician signatory."),
    acknowledgedClientSignatory: z.string().trim().min(1, "Please enter the client representative."),
    acknowledgedSignature: z.string().min(1, "Please click Upload for Acknowledged signature."),
    accomplishedSignature: z.string().min(1, "Please click Upload for Accomplished signature."),
    acknowledgedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Acknowledged signature."),
    accomplishedUploaded: z.boolean().refine(v => v === true, "Please click Upload for Accomplished signature."),
    status: z
      .string()
      .refine((v) => v && v !== "Not Done", {
        message: 'Please set Job Order Status to something other than "Not Done".',
    }),
    control_against_count: z.coerce.number().min(1, "Please select at least one under Control Against."),
    control_against_others: z.coerce.boolean().optional(),
    control_against_others_text: z.string().optional(),
    locations: z.array(LocationRowSchema).min(1, "Add at least one location"),
  }).superRefine((data, ctx) => {
    if (data.control_against_others && !data.control_against_others_text?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["control_against_others_text"],
        message: 'Please specify "Others" under Control Against.',
      });
    }
  }).passthrough();

  const {
    control,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(MakatiSchema),
    mode: "onChange",
    defaultValues: {
      satisfaction_rating: 0,
      acknowledgedSignature: "",
      accomplishedSignature: "",
      acknowledgedUploaded: false,
      accomplishedUploaded: false,
      technician_signatory: "",
      acknowledgedClientSignatory: "",
      status: "",
      control_against_count: 0,
      control_against_others: false,
      control_against_others_text: "",
      locations: [
        {
          location: "",
          pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
          findings: "",
          findings_other: "",
          recommendation: "",
          recommendation_other: "",
        },
      ],
    },
  });

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({ control, name: "locations" });

  // watch the entire locations array
  const allLocations = useWatch({ control, name: "locations" }) || [];

  // initialize totals
  const overallTotals = PEST_OPTIONS.reduce((acc, pest) => {
    acc[pest] = 0;
    return acc;
  }, {});

  // sum each location’s pests (supports array OR object)
  allLocations.forEach((loc) => {
    const np = normalizePests(loc?.pests);
    PEST_OPTIONS.forEach((k) => {
      overallTotals[k] += np[k];
    });
  });

  // grand total
  const grandTotal = Object.values(overallTotals).reduce((a, b) => a + b, 0);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('returnTo');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        setJobOrder(data || {});
        setSatisfactionRating(data?.satisfaction_rating || 0);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client);

        const technician_signed = data?.technicians.map(({ firstname, lastname }) => `${firstname} ${lastname}`);

        setjobOrderParameters((prev) => ({
          ...prev,
          status: data?.status || "",
          inspection_report:  data?.inspection_report || [],
          services:           data?.services          || [],
          pest_problem: data?.pest_problem || "",
          job_date: data?.job_date || "",
          // end_date: data?.end_date || "",
          start_time: data?.start_time || "",
          // end_time: data?.end_time || getNowHHMM(),   // default to current time
          accomplished_by_technician: technician_signed || []
        }));

        setValue("status", data?.status || "", { shouldValidate: true });
        setEditedClient(data.client);
      } catch (error) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) fetchJobOrder();
  }, [id]);


  // for edit tech dialog
  useEffect(() => {
    // fetch once
    getTechnicians().then(data => setAllTechnicians(data || [])).catch(console.error);
  }, []);

  const openEditTechs = () => {
    const ids = (jobOrder?.technicians || []).map(t => t.id);
    setEditSelectedTechIds(ids);
    setEditTechsOpen(true);
  };

  const closeEditTechs = () => setEditTechsOpen(false);

  const toggleEditTech = (techId) => (e) => {
    const checked = e.target.checked;
    setEditSelectedTechIds(prev => checked ? [...prev, techId] : prev.filter(id => id !== techId));
  };

  const saveEditTechs = async () => {
    const selected = (allTechnicians || [])
      .filter(t => editSelectedTechIds.includes(t.id))
      .map(({ id, firstname, lastname }) => ({ id, firstname, lastname }));

    try {
      await putJobOrder(jobOrder.id, { technicians: selected });
      // update local UI immediately
      setJobOrder(prev => ({ ...prev, technicians: selected }));

      // keep the names array in sync for your PDF payload
      setjobOrderParameters(prev => ({
        ...prev,
        accomplished_by_technician: selected.map(t => `${t.firstname} ${t.lastname}`)
      }));

      // ensure signatory stays valid + keep RHF in sync
      const validNames = new Set(selected.map(t => `${t.firstname} ${t.lastname}`));
      const nextSignatory =
        technicianSignatory && validNames.has(technicianSignatory)
          ? technicianSignatory
          : "";

      setTechnicianSignatory(nextSignatory);
      setValue("technician_signatory", nextSignatory, { shouldValidate: true });

      setSuccessMessage("Technicians updated.");
    } catch (err) {
      console.error(err);
      setErrorMessage("Failed to update technicians.");
    } finally {
      setEditTechsOpen(false);
    }
  };

  const onInvalid = () => {
    const first = document.querySelector(".MuiFormHelperText-root.Mui-error");
    first?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Add new states for holding the signature data URLs
  const accomplishedBySignatureRef = useRef(null);
  const acknowledgedBySignatureRef = useRef(null);
  // Declare state for each signature URL
  const [accomplishedSignatureURL, setAccomplishedSignatureURL] = useState("");
  const [acknowledgedSignatureURL, setAcknowledgedSignatureURL] = useState("");

  const [acknowledgedClientSignatory, setAcknowledgedClientSignatory] = useState("");

  // Track when client signature was uploaded (to catch technician delays)
  const [clientSignatureUploadedAt, setClientSignatureUploadedAt] = useState(null);
  // Track when technician signature was uploaded
  const [technicianSignatureUploadedAt, setTechnicianSignatureUploadedAt] = useState(null);

  // Function to recursively convert true/false to True/False in an object
  const convertBooleans = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map((item) => convertBooleans(item));
    } else if (obj && typeof obj === "object") {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [key, convertBooleans(value)])
      );
    } else if (typeof obj === "boolean") {
      return obj ? "True" : "False"; // Convert true/false to 'True'/'False'
    }
    return obj;
  };

  const handleJObOrderChange = (field, value) => {
    setjobOrderParameters(prev => ({ ...prev, [field]: value }));
    if (field === "status") {
      setValue("status", value, { shouldValidate: true, shouldDirty: true });
      if (value !== "Not Done") clearErrors("status");
    }
  };


  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ]

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ]

  // 1. derive a lookup from the raw arrays for label ↔ value mapping
  const formatKey = s => s.toLowerCase().replace(/\s+/g, "_");

  // 2. filter & sort selected job types
  const selectedJobTypes = (jobOrder?.job_types || [])
    // map back to the human-readable label
    .map(val => JOB_TYPES.find(opt => formatKey(opt) === val))
    // remove any non-matches
    .filter(Boolean)
    // alphabetical
    .sort((a, b) => a.localeCompare(b));

  // 3. same for descriptions
  const selectedJobDescriptions = (jobOrder?.job_description || [])
    .map(val => JOB_DESCRIPTIONS.find(opt => formatKey(opt) === val))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));


  const [services, setServices] = useState([
    {
      id: 1,
      activity: "Inspection",
      service: "Initial GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 2,
      activity: "Monitoring",
      service: "Comprehensive GPC",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 3,
      activity: "Spraying",
      service: "Disinfection Treatment",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 4,
      activity: "Misting",
      service: "Exterra Colony Elimination",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 5,
      activity: "Gel Baiting",
      service: "Comprehensive Termite",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 6,
      activity: "Glue Trap",
      service: "Rodent Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 7,
      activity: "Rodent Bait",
      service: "Mosquito Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 8,
      activity: "Rat Cage",
      service: "Cockroach Control",
      activityChecked: false,
      serviceChecked: false,
    },
    {
      id: 9,
      activity: "Wiping",
      service: "Audit / Follow-up",
      activityChecked: false,
      serviceChecked: false,
    },
  ]);

  const handleServicesCheckboxChange = (id, checkboxType) => {
    setServices((prevServices) =>
      prevServices.map((service) =>
        service.id === id
          ? { ...service, [checkboxType]: !service[checkboxType] }
          : service
      )
    );
  };

  const [chemical_used, setChemicalUsed] = useState([
    { label: "Spraying", chemical: "", dilution: "" },
    { label: "Misting", chemical: "", dilution: "" },
    { label: "Trapping", chemical: "", dilution: "" },
    { label: "Rodent Bait", chemical: "", dilution: "" },
    { label: "Gel Bait", chemical: "", dilution: "" },
    { label: "Others", chemical: "", dilution: "" },
  ]);

  const handleChemicalInputChange = (index, field, value) => {
    const newChemicalUsed = [...chemical_used];
    newChemicalUsed[index][field] = value;
    setChemicalUsed(newChemicalUsed);
  };

  const [equipment_used, setEquipmentUsed] = useState([
    { label: "Sprayer", usage: false },
    { label: "Misting Machine", usage: false },
    { label: "Fogging Machine", usage: false },
    { label: "Power Sprayer", usage: false },
    { label: "Others", usage: false },
  ]);

  const handleEquipmentCheckboxChange = (index) => {
    const newEquipmentUsed = [...equipment_used];
    newEquipmentUsed[index].usage = !newEquipmentUsed[index].usage;
    setEquipmentUsed(newEquipmentUsed);
  };

  const [service_report_payment_status, setServiceReportPaymentStatus] =
    useState([
      { label: "Paid", selection: false },
      { label: "COD", selection: false },
      { label: "For Collection", selection: false },
    ]);

  const [service_report_payment_method, setServiceReportPaymentMethod] = useState([
    { label: "Cash", selection: false },
    { label: "Check", selection: false },
    { label: "Free", selection: false },
  ]);

  // Handler for payment status
  const handlePaymentStatusChange = (statusIndex) => {
    const newStatusOptions = service_report_payment_status.map(
      (status, index) => ({
        ...status,
        selection: index === statusIndex ? !status.selection : false,
      })
    );
    setServiceReportPaymentStatus(newStatusOptions);
  };

  // Handler for payment method
  const handlePaymentMethodChange = (methodIndex) => {
    const newMethodOptions = service_report_payment_method.map((method, index) => ({
      ...method,
      selection: index === methodIndex ? !method.selection : false,
    }));
    setServiceReportPaymentMethod(newMethodOptions);
  };

  const handleControlAgainstChange = (field) => (event) => {
    setControlAgainst(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
    if (field === "others" && !event.target.checked) {
     setOtherControlText("");
     clearErrors("control_against_others_text");
   }
  };

  // count how many controlAgainst are checked
  const controlAgainstCount = React.useMemo(
    () => Object.values(controlAgainst).filter(Boolean).length,
    [controlAgainst]
  );

  // push values into RHF so Zod can validate
  React.useEffect(() => {
    setValue("control_against_count", controlAgainstCount, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    setValue("control_against_others", controlAgainst.others, { shouldValidate: true });
    setValue("control_against_others_text", otherControlText, { shouldValidate: true });
  }, [controlAgainstCount, controlAgainst.others, otherControlText, setValue]);

  // End time helper
  // const getNowHHMM = () => {
  //   const d = new Date();
  //   const pad = (n) => String(n).padStart(2, "0");
  //   return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  // };


  const saveAndUploadAccomplishedSignature = async () => {
    try {
      setIsLoading(true);

      // ⏰ CAPTURE TECHNICIAN SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setTechnicianSignatureUploadedAt(uploadTimestamp);

      const url = await resizeAndUploadSignature(accomplishedBySignatureRef, jobOrder.id, "accomplished");
      setAccomplishedSignatureURL(url);
      // ✅ tell RHF that upload succeeded
      setValue("accomplishedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("accomplishedUploaded", true, { shouldValidate: true });
      clearErrors(["accomplishedSignature", "accomplishedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        technician_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      console.error("Error saving signature:", error);
      setErrorMessage(`Error saving signature: ${error.message}`);
      setValue("accomplishedSignature", "", { shouldValidate: true });
      setValue("accomplishedUploaded", false, { shouldValidate: true });
      setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAccomplishedSignature = () => {
    accomplishedBySignatureRef.current?.clear();
    setAccomplishedSignatureURL("");
    setValue("accomplishedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("accomplishedUploaded", false, { shouldValidate: true });
    setError("accomplishedUploaded", { type: "manual", message: "Please click Upload for Accomplished signature." });
  };

  const saveAndUploadAcknowledgedSignature = async () => {
    try {
      setIsLoading(true);
      const url = await resizeAndUploadSignature(acknowledgedBySignatureRef, jobOrder.id, "acknowledged");

      // ⏰ CAPTURE CLIENT SIGNATURE UPLOAD TIMESTAMP
      const uploadTimestamp = new Date().toISOString();
      setClientSignatureUploadedAt(uploadTimestamp);

      setAcknowledgedSignatureURL(url);
      setValue("acknowledgedSignature", url, { shouldValidate: true, shouldDirty: true });
      setValue("acknowledgedUploaded", true, { shouldValidate: true });
      clearErrors(["acknowledgedSignature", "acknowledgedUploaded"]);

      // 💾 IMMEDIATELY SAVE TIMESTAMP TO BACKEND (to prevent cheating)
      await putJobOrder(jobOrder.id, {
        client_signature_uploaded_at: uploadTimestamp
      });
      // Timestamp saved (hidden from logs for security)

      setSuccessMessage("Signature successfully saved!");
    } catch (error) {
      setErrorMessage("Error saving signature. Please try again.");
      setValue("acknowledgedSignature", "", { shouldValidate: true });
      setValue("acknowledgedUploaded", false, { shouldValidate: true });
      setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
    } finally {
      setIsLoading(false);
    }
  };

  const clearAcknowledgedSignature = () => {
    acknowledgedBySignatureRef.current?.clear();
    setAcknowledgedSignatureURL("");
    setValue("acknowledgedSignature", "", { shouldValidate: true, shouldDirty: true });
    setValue("acknowledgedUploaded", false, { shouldValidate: true });
    setError("acknowledgedUploaded", { type: "manual", message: "Please click Upload for Acknowledged signature." });
  };

  const resizeAndUploadSignature = async (signatureRef, jobOrderId, type) => {
    // 1. grab the trimmed canvas
    if (!signatureRef.current) throw new Error("No signature canvas available");
    const canvas = signatureRef.current.getTrimmedCanvas();
    if (!canvas) throw new Error("Nothing to upload");

    // 2. draw it into a fixed‐size canvas
    const size = 200;
    const c2 = document.createElement("canvas");
    c2.width = size;
    c2.height = size;
    c2.getContext("2d").drawImage(canvas, 0, 0, size, size);

    // 3. turn into a blob, rejecting if null
    const blob = await new Promise((resolve, reject) => {
      c2.toBlob(b => (b ? resolve(b) : reject(new Error("Canvas toBlob returned null"))), "image/png");
    });

    // 4. upload
    const key = `${jobOrderId}-signature-${type}.png`;
    const params = {
      Bucket: TEMP_BUCKET_NAME,
      Key: key,
      Body: blob,
      ContentType: "image/png",
    };
    console.log("Uploading signature:", params);

    const resp = await s3TempBucketUploader.upload(params).promise();
    // AWS SDK v2 returns resp.Location; if not, you can fall back to the public URL:
    return resp.Location || `https://${params.Bucket}.s3.amazonaws.com/${params.Key}`;
  };

  const handleGeneratePDF = async (type) => {
    // Merge contact details with jobOrderParameters
    const pdfData = {
      ...jobOrderParameters,
      accomplishedSignature: accomplishedSignatureURL,
      acknowledgedSignature: acknowledgedSignatureURL,
    };

    console.log("Job Order Parameters:", pdfData);

    try {
      setIsLoading(true);
      const pdfBlob =
        type === "invoice"
          ? await generateReceipts(pdfData)
          : await generateReports(pdfData);
      if (!pdfBlob) {
        throw new Error("Failed to generate PDF blob.");
      }
      const s3Url = await uploadPDF(pdfBlob, jobOrder.id, type);
      console.log(`${type} PDF uploaded to S3 and DynamoDB updated:`, s3Url);

      // Prepare update data with the correct link field
      const updateData = {
        [`${type === "invoice" ? "receipts_url" : "reports_url"}`]: s3Url,
      };

      // Update DynamoDB with the S3 URL
      await patchJobOrderLink(jobOrder.id, updateData);
      console.log(`DynamoDB updated with ${type} URL:`, s3Url);
    } catch (error) {
      console.error("Error generating or uploading PDF:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadPDF = async (pdfBlob, jobOrderId, type) => {
    console.log("Generated PDF Blob:", pdfBlob);

    const fileName = `${jobOrderId}-${type}.pdf`;
    const s3Uploader =
      type === "invoice" ? s3InvoicePDFUploader : s3ReportsPDFUploader;
    const params = {
      Bucket: type === "invoice" ? INVOICE_BUCKET_NAME : REPORTS_BUCKET_NAME,
      Key: `${fileName}`,
      Body: pdfBlob,
      ContentType: "application/pdf",
    };

    console.log("S3 Upload Parameters:", params); // Debugging line to view upload parameters

    try {
      await s3Uploader.upload(params).promise();
      return `https://${params.Bucket}.s3.amazonaws.com/${fileName}`;
    } catch (error) {
      console.error("S3 upload error:", error);
      throw new Error("Upload failed");
    }
  }; 

  // Function to format date
  // TODO: you can use date-fns for more advanced date formatting
  function formatDate(dateString) {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        throw new Error("Invalid date format");
      }

      return new Intl.DateTimeFormat("en-US", options).format(date);
    } catch (error) {
      console.error(error.message);
      return "Invalid Date"; // Fallback for invalid dates
    }
  }

  const formattedDate = formatDate(jobOrderParameters.job_date);

  const handleSendEmail = async () => {
    const generateAttachmentLinks = (links, False) => {
      // Determine which keys to use for the links
      const baseUrl = `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}`;
      return {
        reports_url: `${baseUrl}/${jobOrder.id}_report.pdf`,
        certificate_url: `${baseUrl}/${jobOrder.id}_certificate.pdf`,
        receipts_url: `${baseUrl}/${jobOrder.id}_final_receipt.pdf`
      };
    };

    // Generate the attachment links
    const attachmentLinks = generateAttachmentLinks();

    // Create the HTML list items from the links
    const attachmentLinksHTML = Object.entries(attachmentLinks)
      .map(([key, url]) => {
        const fileName = key
          .replace(/_url$/, "")
          .replace(/_/g, " ")
          .toUpperCase(); // Remove '_url' and format
        return `<li><a href="${url}" target="_blank">${fileName}</a></li>`;
      })
      .join("");

    const htmlBody = `
    <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
          }
          .email-container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            
            border: 1px solid #ddd;
          }
          .header, .footer {
            width: 100%;
            text-align: center;
            margin: 0;
            padding: 20px 0;
            
          }
          .content {
            margin: 20px 0;
          }
           .content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0 auto;
          }
        </style>
      </head>
      <body>
        <div class="email-container">
          <!-- Header Section -->
          <div class="header">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/header_boaz_transparent1.png" alt="Boaz Header" style="max-width: 100%; height: auto;">
          </div>

          <!-- Main Content -->
          <div class="content">
            <body>            
            <p>Sir/Ma'am,</p>
            <p></p>
            <p>Good day!</p>
            <p></p>
            <p>We are pleased to send you our Accomplishment Report for the visit done on  ${formattedDate}.</p>
            <p>We thank you and please let us know if have any questions.</p>
            <p></p>            
            <p>Sincerely,</p>
            <p>Boaz Pest Control - Accounts Team</p>
            <ul>
              ${attachmentLinksHTML}
            </ul>
            <hr />                     
          </body>            
          </div>

          <!-- Footer Section -->
          <a href="mailto:boazpestcontrol@gmail.com" style="text-decoration: none;">
          <div class="footer">
            <img src="https://boaz-email-images.s3.ap-southeast-1.amazonaws.com/footer_boaz_transparent1.png" alt="Boaz Footer" style="max-width: 100%; height: auto;">
            <hr />
            <footer>              
              <p>If you no longer wish to receive these emails, you can <a href="mailto:unsubscribe-boaz@maestrosystemsinc.com">unsubscribe here</a>.</p>
            </footer>
          </div>
          </a>
        </div>
      </body>
    </html>`;

    const lambdaPayload = {
      // from: "boaz-billing@maestrosystemsinc.com",
      from: "boaz-reminders@maestrosystemsinc.com",
      recipients: [jobOrder?.client?.contact?.email?.email],
      subject: `Service Report for Job Order ${jobOrder?.id}`,
      body: htmlBody,
    };

    try {
      // Only send email if recipient email exists
      if (jobOrder?.client?.contact?.email?.email) {
        await sendEmailQueue(lambdaPayload);
        console.log("✅ Email notification sent successfully");
      } else {
        console.warn("⚠️ Email notification skipped - no client email address");
      }
    } catch (error) {
      // Log but don't show error to user - email is optional
      console.warn("⚠️ Email notification failed (non-critical):", error.message);
    }
  };

  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  // Determine numeric dimensions based on breakpoints
  let canvasWidth, canvasHeight;
  if (isLg) {
    canvasWidth = 300;
    canvasHeight = 350;
  } else if (isMd) {
    canvasWidth = 250;
    canvasHeight = 325;
  } else {
    canvasWidth = 200;
    canvasHeight = 300;
  }

  let ackWidth, ackHeight;
  if (isLg) {
    ackWidth = 300;
    ackHeight = 150;
  } else if (isMd) {
    ackWidth = 250;
    ackHeight = 125;
  } else {
    ackWidth = 200;
    ackHeight = 100;
  }

  const onSubmit = async (formData) => {
    setIsLoading(true);

    const inspection_report = Array.from({ length: 15 }).map((_, idx) => {
      const loc = allLocations[idx] || {
        location: "",
        pests: {}, // <- default to object now
        findings: "",
        findings_other: "",
        recommendation: "",
        recommendation_other: "",
      };

      const findings = (loc.findings_other?.trim() || loc.findings || "").trim();
      const recommendation = (loc.recommendation_other?.trim() || loc.recommendation || "").trim();

      const pests = normalizePests(loc.pests); // unify shape
      const total = PEST_OPTIONS.reduce((sum, k) => sum + pests[k], 0);

      return {
        location: loc.location || "",
        pests,
        total: String(total),
        findings,
        recommendation,
      };
    });

    const updatedJobOrderParameters = {
      ...jobOrderParameters,
      inspection_report,
      services,
      chemical_used,
      equipment_used,
      service_report_payment_status,
      service_report_payment_method,
      satisfaction_rating: formData.satisfaction_rating,
      accomplishedSignature: formData.accomplishedSignature,
      acknowledgedSignature: formData.acknowledgedSignature,
      acknowledgedClientSignatory: formData.acknowledgedClientSignatory,
      control_against: {
        crawling_insects: controlAgainst.crawlingInsects,
        flying_insects:   controlAgainst.flyingInsects,
        rodent_control:   controlAgainst.rodentControl,
        others:           controlAgainst.others,
        others_text:      otherControlText.trim()
      },
      service_report_status: "Done",
      technician_signatory: formData.technician_signatory,
      technicians: jobOrder?.technicians || [],
      // ⏰ TIMESTAMP TRACKING (prevents technician time-cheating)
      client_signature_uploaded_at: clientSignatureUploadedAt,  // When UPLOAD was clicked
      report_submitted_at: new Date().toISOString(),  // When SUBMIT was clicked
    };

    const { id, ...putClientParameters } = editedClient;

    //Added for Document Generation Receipts, Reports, Certificate
    try {
      // 1) transform & convert booleans
      const payload = convertBooleans({ jobOrder, updatedJobOrderParameters, form: formData });
      // 2) call your PDF “field-mapper”
      const response = await submitDocumentFieldMapper(payload);
      console.log("Job order parameters submitted successfully:", response);

      setLinks(
        {
          reports_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_report.pdf`,
          certificate_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_certificate.pdf`,
          receipts_url: `https://boaz-joborder-generated.s3.amazonaws.com/${jobOrder.id}/${jobOrder.id}_final_receipt.pdf`,
        }
      );

      // Send the email after updating links

      setSuccessMessage(
        "Service Report and documents and generated successfully! Redirecting..."
      );

      setIsLoading(false);
      // analysis
      await handleSendEmail();

      // 💾 SAVE ALL UPDATED PARAMETERS TO DATABASE (including timestamps)
      await putJobOrder(jobOrder.id, updatedJobOrderParameters);
      await putClient(id, putClientParameters);
      // All parameters saved successfully

      // Redirect to service reports list after 2 seconds
      setTimeout(() => {
        navigate("/manage-service-reports");
      }, 2000);
    } catch (error) {
      console.error("Error submitting job order parameters:", error);
      setErrorMessage(
        `Failed to generate Service Report and Documents: ${error.message}. Please check your network connection and try again.`
      );
    } finally {
      setIsLoading(false); // Set loading to false
    }
  }

  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon sx={{ fontSize: "40px", marginRight: "8px" }} />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="Create Service Report"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form onSubmit={handleFormSubmit(onSubmit, onInvalid)} className="job-order-form">
        <Grid container>
          {/* Basic Information Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginTop: { xs: "-5px", md: "5px", lg: "10px", xl: "20px" },
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {" "}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <TextField
                      name="id"
                      label="Job Order ID"
                      value={jobOrder?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={2}>
                    <FormControl fullWidth size="small" error={!!errors.status}>
                      <InputLabel id="job-order-status-label">
                        Job Order Status
                      </InputLabel>
                      <Select
                        labelId="job-order-status-label"
                        name="status"
                        value={jobOrderParameters?.status || ""}
                        label="Job Order Status"
                        onChange={(e) =>
                          handleJObOrderChange("status", e.target.value)
                        }
                      >
                        <MenuItem value="Not Done">Not Done</MenuItem>
                        <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                        <MenuItem value="Done">Done</MenuItem>
                        <MenuItem value="Cancelled By Client">
                          Cancelled By Client
                        </MenuItem>
                      </Select>
                      {errors.status && (
                        <FormHelperText>{errors.status.message}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client ID"
                      label="Client ID"
                      value={jobOrder?.client?.id || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      name="Client Name"
                      label="Client Name"
                      value={jobOrder?.client?.name || ""}
                      fullWidth
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={12} lg={12} xl={10}>
                        <TextField
                          label="Job Site Address"
                          variant="outlined"
                          fullWidth
                          value={
                            jobOrder?.client?.address
                              ? `${jobOrder?.client?.address.house_unit_street || ""
                              }, ${jobOrder?.client?.address.barangay || ""
                              }, ${jobOrder?.client?.address.city || ""
                              }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder?.client?.address.zipcode || ""
                              }`
                              : ""
                          }
                          disabled
                          size="small"
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12} xl={2}>
                        <TextField
                          name="area"
                          label="Area"
                          value={jobOrder?.client?.address?.area || ""}
                          fullWidth
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Person"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact
                          ? `${jobOrder?.client?.contact.lastname || ""}, ${jobOrder?.client?.contact.firstname || ""
                          }`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      variant="outlined"
                      value={jobOrder?.client?.contact?.email?.email || ""}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} xl={4}>
                    <TextField
                      fullWidth
                      label="Contact Number"
                      variant="outlined"
                      value={
                        jobOrder?.client?.contact?.number
                          ? `${jobOrder?.client?.contact.number.type}: ${jobOrder?.client?.contact.number.number}`
                          : ""
                      }
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={4}>
                    <TextField
                      label="Location Type"
                      value={
                        client?.location_type
                          ? client.location_type
                          : ""
                      }
                      fullWidth
                      variant="outlined"
                      sx={{ fontSize: "0.875rem" }}
                      disabled
                    />
                  </Grid>           
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Type Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Type</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobTypes.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Job Description Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={<Typography variant="subtitle2">Job Description</Typography>}
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {selectedJobDescriptions.map(option => (
                    <Box
                      component="li"
                      key={option}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        mb: 0.5,
                      }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                      <Typography variant="body2">{option}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Special Notes Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Special Notes
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Special Notes"
                      name="special_notes"
                      type="text"
                      value={jobOrder?.special_notes || ""}
                      fullWidth
                      disabled
                      multiline
                      rows={4}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Appointment Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Appointment
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {/* Appointment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="appointment-type-label">
                        Appointment Type
                      </InputLabel>
                      <Select
                        labelId="appointment-type-label"
                        value={jobOrder?.appointment_type || ""}
                        label="Appointment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="Initial">Initial</MenuItem>
                        <MenuItem value="Follow-up">Follow-up</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Treatment Type */}
                  <Grid item xs={12} md={6} lg={6} xl={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="treatment-type-label">
                        Treatment Type
                      </InputLabel>
                      <Select
                        labelId="treatment-type-label"
                        value={jobOrder?.treatment_type || ""}
                        label="Treatment Type"
                        disabled
                      >
                        <MenuItem value="">—</MenuItem>
                        <MenuItem value="QB">QB</MenuItem>
                        <MenuItem value="FFUP">FFUP</MenuItem>
                        <MenuItem value="LT">LT</MenuItem>
                        <MenuItem value="QBLT">QBLT</MenuItem>
                        <MenuItem value="Backjob">Backjob</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Inspection & Pest Count (Mobile) */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                    Inspection Report
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <Stack spacing={2}>
                  {locationFields.map((loc, idx) => (
                    <LocationCard
                      key={loc.id}
                      control={control}
                      register={register}
                      index={idx}
                      removeLocation={removeLocation}
                    />
                  ))}

                  <Button
                    variant="contained"
                    disabled={locationFields.length >= 15}
                    fullWidth
                    onClick={() =>
                      appendLocation({
                        location: "",
                        // pests: [{ type: "", count: "" }],
                        pests: { Roaches: 0, Mosq: 0, Rodents: 0, Flies: 0, Ants: 0, Others: 0 },
                        findings: "",
                        findings_other: "",
                        recommendation: "",
                        recommendation_other: "",
                      })
                    }
                  >
                    Add New Location ({locationFields.length}/15)
                  </Button>

                  {/* Overall Total Pests Section */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ mt: 2 }}>
                      <CardHeader
                        title={
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                          >
                            OVERALL TOTAL PESTS
                          </Typography>
                        }
                        sx={{ pb: 0, px: 2, pt: 2 }}
                      />
                      <CardContent sx={{ px: 2, pb: 2 }}>
                        {PEST_OPTIONS.map((pest) => (
                          <Typography
                            key={pest}
                            variant="body2"
                            sx={{ display: "flex", justifyContent: "space-between" }}
                          >
                            {pest}
                            <span>{overallTotals[pest] || 0}</span>
                          </Typography>
                        ))}

                        <Typography
                          variant="subtitle1"
                          sx={{ mt: 1, fontWeight: "bold", display: "flex", justifyContent: "space-between" }}
                        >
                          Total
                          <span>{grandTotal}</span>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Services Performed */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardHeader
                title={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Services Performed
                  </Typography>
                }
                sx={{ p: 2, pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {services
                    // grab all service labels
                    .map((s) => s.service)
                    // sort them alphabetically
                    .sort((a, b) => a.localeCompare(b))
                    // render each with its own checkbox
                    .map((service) => {
                      const item = services.find((s) => s.service === service);
                      return (
                        <Box
                          component="li"
                          key={service}
                          sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                        >
                          <Checkbox
                            checked={item.serviceChecked}
                            onChange={() =>
                              handleServicesCheckboxChange(item.id, 'serviceChecked')
                            }
                          />
                          <Typography variant="body2">{service}</Typography>
                        </Box>
                      );
                    })}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Activities Done */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                  >
                    Activities Done
                  </Typography>
                }
                sx={{ p: 2, pb: 0 }}
              />
              <CardContent sx={{ pt: 1, px: 2 }}>
                <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                  {services
                    // grab all activity labels
                    .map((s) => s.activity)
                    // sort them alphabetically
                    .sort((a, b) => a.localeCompare(b))
                    // render each with its own checkbox
                    .map((activity) => {
                      const item = services.find((s) => s.activity === activity);
                      return (
                        <Box
                          component="li"
                          key={activity}
                          sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}
                        >
                          <Checkbox
                            checked={item.activityChecked}
                            onChange={() =>
                              handleServicesCheckboxChange(item.id, 'activityChecked')
                            }
                          />
                          <Typography variant="body2">{activity}</Typography>
                        </Box>
                      );
                    })}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Reminder Notes */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Reminder Notes
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Reminder Notes"
                  value={jobOrderParameters?.reminder_notes || ""}
                  onChange={e =>
                    handleJObOrderChange("reminder_notes", e.target.value)
                  }
                  fullWidth
                  multiline
                  rows={6}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Chemical Used Section */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                <Grid item xs={12}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Chemical Used / Batch # / Expiration
                          </TableCell>
                          <TableCell
                            align="center"
                            style={{ fontWeight: "bold" }}
                          >
                            Dilution
                          </TableCell>
                          <TableCell
                            align="center"
                            colSpan={2}
                            style={{ fontWeight: "bold" }}
                          >
                            Equipment Used
                          </TableCell>                          
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chemical_used.map((chemical, index) => (
                          <TableRow key={index}>
                            <TableCell>{chemical.label}:</TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.chemical}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "chemical",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 100 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={chemical.dilution}
                                onChange={(e) =>
                                  handleChemicalInputChange(
                                    index,
                                    "dilution",
                                    e.target.value
                                  )
                                }
                                sx={{ minWidth: 50 }}
                              />
                            </TableCell>
                            {index < equipment_used.length ? (
                              <>
                                <TableCell>
                                  {equipment_used[index].label}
                                </TableCell>
                                <TableCell>
                                  <Checkbox
                                    checked={equipment_used[index].usage}
                                    onChange={() =>
                                      handleEquipmentCheckboxChange(index)
                                    }
                                  />
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                              </>
                            )}                            
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Control Against Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", textTransform: "uppercase" }}
                  >
                    CONTROL AGAINST
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <FormGroup sx={{ width: "100%" }}>
                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.crawlingInsects}
                        onChange={handleControlAgainstChange("crawlingInsects")}
                      />
                    }
                    label="Crawling Insects"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.flyingInsects}
                        onChange={handleControlAgainstChange("flyingInsects")}
                      />
                    }
                    label="Flying Insects"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.rodentControl}
                        onChange={handleControlAgainstChange("rodentControl")}
                      />
                    }
                    label="Rodent Control"
                  />

                  <FormControlLabel
                    sx={{ width: "100%" }}
                    control={
                      <Checkbox
                        checked={controlAgainst.others}
                        onChange={handleControlAgainstChange("others")}
                      />
                    }
                    label="Others"
                  />
                </FormGroup>

                {controlAgainst.others && (
                  <TextField
                    label="Please specify"
                    value={otherControlText}
                    onChange={(e) => setOtherControlText(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
              {errors?.control_against_count && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.control_against_count.message}
                </FormHelperText>
              )}

              {controlAgainst.others && errors?.control_against_others_text && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {errors.control_against_others_text.message}
                </FormHelperText>
              )}
            </Card>
          </Grid>
          <input type="hidden" {...register("control_against_count")} />
          <input type="hidden" {...register("control_against_others")} />
          <input type="hidden" {...register("control_against_others_text")} />

          {/* Service Fee */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                    Service Fee
                  </Typography>
                }
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TextField
                  label="Service Fee"
                  value={jobOrderParameters?.service_fee || ""}
                  onChange={e =>
                    handleJObOrderChange("service_fee", e.target.value)
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₱</InputAdornment>
                  }}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Status Section */}
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={<Typography variant="h6">Payment Status</Typography>}
                sx={{ pb: 0, px: 2, pt: 2 }}
              />
              <CardContent sx={{ px: 2, pb: 2 }}>
                <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                  <Table size="small">
                    <TableBody>
                      {(service_report_payment_status || []).map((statusOpt, idx) => {
                        const methodOpt = (service_report_payment_method || [])[idx] || {};
                        return (
                          <TableRow key={statusOpt.label} hover>
                            <TableCell>{statusOpt.label}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={statusOpt.selection}
                                onChange={() => handlePaymentStatusChange(idx)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ color: 'text.secondary', p: 0 }}>
                              •
                            </TableCell>
                            <TableCell/>
                            <TableCell>{methodOpt.label || ""}</TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={methodOpt.selection}
                                onChange={() => handlePaymentMethodChange(idx)}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Accomplished By and Acknowledged By Signature Section */}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    {/* Service Accomplished by Section */}
                    <Grid item xs={12} md={12}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 2 }}>
                            Service Accomplished by:
                          </Typography>
                          <Button size="small" variant="outlined" onClick={openEditTechs} sx={{ mb: 2 }}>
                            Edit technicians
                          </Button>
                        </Grid>
                      </Grid>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={1}>
                            {jobOrder?.technicians?.length > 0 ? (
                              jobOrder?.technicians.map((technician, index) => (
                                <Grid
                                  item
                                  xs={12}
                                  md={12}
                                  lg={12}
                                  xl={12}
                                  key={technician.id}
                                  mb={2}
                                >
                                  <TextField
                                    label={`Technician ${index + 1}`}
                                    variant="outlined"
                                    fullWidth
                                    value={`${technician.firstname} ${technician.lastname}`}
                                    disabled
                                    size="small"
                                  />
                                </Grid>
                              ))
                            ) : (
                              <Grid item xs={12} md={12} lg={12} xl={12}>
                                <TextField
                                  label="Technician"
                                  variant="outlined"
                                  fullWidth
                                  value="No Technicians Available"
                                  InputProps={{
                                    readOnly: true,
                                  }}
                                  size="small"
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                        <Grid item xs={12} md={6}>                          
                          <Box
                            sx={{
                              width: canvasWidth,
                              height: ackHeight,
                              border: "2px solid #000",
                              padding: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                            }}
                          >
                            <SignatureCanvas
                              ref={accomplishedBySignatureRef}
                              penColor="black"
                              canvasProps={{
                                width: canvasWidth,
                                height: ackHeight,
                                className: "sigCanvas",
                              }}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Technician Signatory Dropdown */}
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal" error={!!errors.technician_signatory}>
                          <InputLabel id="technician-signatory-label">
                            Technician Signatory
                          </InputLabel>
                          <Select
                            labelId="technician-signatory-label"
                            id="accomplished-by-technician"
                            value={technicianSignatory}
                            label="Technician Signatory"
                            onChange={(e) => {
                              const val = e.target.value;
                              setTechnicianSignatory(val);                       // keep local state (your UI)
                              setValue("technician_signatory", val, {            // reflect into RHF for validation
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              clearErrors("technician_signatory");
                            }}
                          >
                            {jobOrder?.technicians?.map((tech) => (
                              <MenuItem
                                key={tech.id}
                                value={`${tech.firstname} ${tech.lastname}`}
                              >
                                {`${tech.firstname} ${tech.lastname}`}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.technician_signatory && (
                            <FormHelperText error>{errors.technician_signatory.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <input type="hidden" {...register("technician_signatory")} />

                      {/* Action Buttons for Accomplished Signature */}
                      <Grid
                        item
                        xs={12}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        mt={2}
                        mb={2}
                        mr={2}
                      >
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={clearAccomplishedSignature}
                          sx={{
                            fontSize: "0.875rem",
                            color: "#FF4C4C",
                            borderColor: "#FF4C4C",
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={saveAndUploadAccomplishedSignature}
                          sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                        >
                          Upload
                        </Button>
                      </Grid>
                      {(errors?.accomplishedUploaded || errors?.accomplishedSignature) && (
                        <FormHelperText error sx={{ mt: 1 }}>
                          {errors.accomplishedUploaded?.message || errors.accomplishedSignature?.message}
                        </FormHelperText>
                      )}

                      {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                      {!isTechnician && technicianSignatureUploadedAt && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                            🔵 Technician Signature Uploaded At:
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                            {new Date(technicianSignatureUploadedAt).toLocaleString('en-US', {
                              month: 'short',
                              day: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                            This timestamp is saved and cannot be changed.
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Schedule Section */}
            <Grid
              item
              xs={12}
              md={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      sx={{                      
                        fontWeight: "bold",
                        textTransform: "uppercase",
                        fontSize: "1rem",
                      }}
                    >
                      Schedule
                    </Typography>
                  }
                />
                <CardContent sx={{ flex: 1 }}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Date"
                        name="job_date"
                        type="date"
                        value={jobOrderParameters.job_date}
                        onChange={(e) =>
                          handleJObOrderChange("job_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Start Time"
                        name="start_time"
                        type="time"
                        value={jobOrderParameters.start_time}
                        onChange={(e) =>
                          handleJObOrderChange("start_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        label="End Date"
                        name="end_date"
                        type="date"
                        value={jobOrderParameters.end_date}
                        onChange={(e) =>
                          handleJObOrderChange("end_date", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            min: jobOrderParameters.job_date || undefined
                          }
                        }}
                      />
                    </Grid> */}
                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        label="End Time"
                        name="end_time"
                        type="time"
                        value={jobOrderParameters.end_time}
                        onChange={(e) =>
                          handleJObOrderChange("end_time", e.target.value)
                        }
                        fullWidth
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                onClick={() => handleJObOrderChange("end_time", getNowHHMM())}
                              >
                                Now
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid> */}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* For Client Only Section */}
            <Grid item xs={12}>
              <Card
                variant="outlined"
                sx={{
                  marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                  flex: 1,
                }}
              >
                <CardHeader
                  title={
                    <Typography variant="subtitle2"
                                sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                      For Client Only
                    </Typography>
                  }
                  sx={{ px: 2, pt: 2, pb: 0 }}
                />

                <CardContent sx={{ px: 2, pb: 2 }}>
                  {/* Satisfaction label */}
                  <Typography variant="body2">
                    Your Satisfaction Rating:                    
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    (1 – lowest, 5 – highest)
                  </Typography>

                  {/* Rating buttons */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    {[1, 2, 3, 4, 5].map((n) => {
                      const selected = satisfactionRating === n;
                      return (
                        <Button
                          key={n}
                          onClick={() => {
                            setSatisfactionRating(n);
                            setjobOrderParameters(prev => ({
                              ...prev,
                              satisfaction_rating: n
                            }));
                            // ✅ reflect to RHF so validation passes & errors disappear
                            setValue("satisfaction_rating", n, { shouldValidate: true, shouldDirty: true });
                            clearErrors("satisfaction_rating");
                          }}
                          sx={{
                            fontSize: '1.25rem',
                            minWidth: '40px',
                            height: '40px',
                            p: 0,
                            borderRadius: '50%',
                            transition: 'all 0.2s ease-in-out',
                            // selected styles
                            bgcolor: selected ? 'primary.main' : 'transparent',
                            color: selected ? 'common.white' : 'text.secondary',
                            border: selected ? 'none' : '1px solid',
                            borderColor: selected ? 'transparent' : 'divider',
                            transform: selected ? 'scale(1.2)' : 'scale(1)',
                            '&:hover': {
                              bgcolor: selected ? 'primary.dark' : 'grey.100',
                            },
                            cursor: 'pointer'
                          }}
                        >
                          {n}
                        </Button>
                      );
                    })}
                  </Box>
                  {errors?.satisfaction_rating && (
                    <FormHelperText error sx={{ mt: 1 }}>
                      {errors.satisfaction_rating.message}
                    </FormHelperText>
                  )}
                
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Acknowledged By:
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>                        
                        <Box
                          sx={{
                            width: ackWidth,
                            height: ackHeight,
                            border: "2px solid #000",
                            padding: 1,
                            marginTop: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <SignatureCanvas
                            ref={acknowledgedBySignatureRef}
                            penColor="black"
                            canvasProps={{
                              width: ackWidth,
                              height: ackHeight,
                              className: "sigCanvas",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} sx={{ marginTop: 2 }}>
                      <Grid item xs={12} md={12} lg={12}>                        
                        <TextField
                          fullWidth
                          label="Client Representative"
                          name="Client Representative"
                          variant="outlined"
                          value={acknowledgedClientSignatory}
                          onChange={(e) => {
                            const val = e.target.value;
                            setAcknowledgedClientSignatory(val);                 // keep local state
                            setValue("acknowledgedClientSignatory", val, {       // reflect into RHF
                              shouldValidate: true,
                              shouldDirty: true,
                            });
                            clearErrors("acknowledgedClientSignatory");
                          }}
                          size="small"
                        />
                        {errors.acknowledgedClientSignatory && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {errors.acknowledgedClientSignatory.message}
                          </FormHelperText>
                        )}
                      </Grid>
                    </Grid>
                    <input type="hidden" {...register("acknowledgedClientSignatory")} />

                    {/* Action Buttons for Acknowledged Signature */}
                    <Grid
                      item
                      xs={12}
                      display="flex"
                      justifyContent="flex-start"
                      alignItems="center"
                      mt={2}
                      mb={2}
                      mr={2}
                    >
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={clearAcknowledgedSignature}
                        sx={{
                          fontSize: "0.875rem",
                          color: "#FF4C4C",
                          borderColor: "#FF4C4C",
                        }}
                      >
                        Clear
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={saveAndUploadAcknowledgedSignature}
                        sx={{ marginLeft: "10px", fontSize: "0.875rem" }}
                      >
                        Upload
                      </Button>
                    </Grid>
                    {(errors?.acknowledgedUploaded || errors?.acknowledgedSignature) && (
                      <FormHelperText error sx={{ mt: 1 }}>
                        {errors.acknowledgedUploaded?.message || errors.acknowledgedSignature?.message}
                      </FormHelperText>
                    )}

                    {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
                    {!isTechnician && clientSignatureUploadedAt && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                          ✅ Client Signature Uploaded At:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                          {new Date(clientSignatureUploadedAt).toLocaleString('en-US', {
                            month: 'short',
                            day: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#558b2f', mt: 0.5, display: 'block' }}>
                          This timestamp is saved and cannot be changed.
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </CardContent>

              </Card>
            </Grid>
          </Grid>
          
          {/* Dialog for Edit Technicians */}
          <Dialog open={editTechsOpen} onClose={closeEditTechs} maxWidth="xs" fullWidth>
            <DialogTitle>Edit Technicians</DialogTitle>
            <DialogContent dividers>
              <Table size="small">
                <TableBody>
                  {allTechnicians.map(t => {
                    const notes = [];
                    if (t.competency_level?.driving_4_wheels && t.competency_level.driving_4_wheels !== "N/A") {
                      notes.push("Driver: 4 wheels");
                    }
                    if (t.competency_level?.driving_2_wheels && t.competency_level.driving_2_wheels !== "N/A") {
                      notes.push("Driver: 2 wheels");
                    }
                    const checked = editSelectedTechIds.includes(t.id);
                    return (
                      <TableRow key={t.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked} onChange={toggleEditTech(t.id)} />
                        </TableCell>
                        <TableCell>{`${t.lastname}, ${t.firstname}`}</TableCell>
                        <TableCell>{notes.join(", ")}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditTechs}>Cancel</Button>
              <Button variant="contained" disabled={!editSelectedTechIds.length} onClick={saveEditTechs}>
                Save
              </Button>
            </DialogActions>
          </Dialog>

          {/* Display Receipts and Reports Links */}
          <Grid
            item
            xs={12}
            md={12}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "1rem",
                    }}
                  >
                    Document Links
                  </Typography>
                }
              />
              <CardContent sx={{ flex: 1 }}>
                <Grid container spacing={3}>
                  {links?.receipts_url ||
                    links?.certificate_url ||
                    links?.reports_url ? (
                    <>
                      {links?.receipts_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.receipts_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Receipts.pdf{" "}
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.certificate_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.certificate_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Certificate.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}

                      {links?.reports_url && (
                        <Grid item xs={12}>
                          <Box>
                            <Link
                              href={links.reports_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              Reports.pdf
                              <OpenInNewIcon
                                sx={{
                                  marginLeft: 1,
                                  fontSize: "1rem",
                                }}
                              />
                            </Link>
                          </Box>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography variant="body1" color="textSecondary">
                        No document links available.
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Form buttons */}
          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                if (returnTo) {
                  navigate(returnTo);
                } else {
                  navigate("/manage-service-reports");
                }
              }}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
        <Controller name="acknowledgedSignature" control={control} rules={{ required: true }} render={() => null} />
        <Controller name="acknowledgedUploaded"  control={control} rules={{ validate: v => v === true || "Please click Upload for Acknowledged signature." }} render={() => null} />
        <Controller name="accomplishedSignature" control={control} rules={{ required: true }} render={() => null} />
        <Controller name="accomplishedUploaded"  control={control} rules={{ validate: v => v === true || "Please click Upload for Accomplished signature." }} render={() => null} />
        <Controller name="satisfaction_rating"   control={control} rules={{ required: true, validate: v => Number(v) >= 1 || "Please pick a rating (1–5)." }} render={() => null} />
      </form>
    </BaseFormWrapper>
  );
}

export default MakatiGpcForm;
